
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
import { ShinseiBunruiModel } from "@/interfaces/shinsei.interface";
import {
  checkShinseiBunruiCd,
  createShinseiBunrui,
  getShinseiBunruiById,
  updateShinseiBunrui,
} from "@/api/shinsei.api";

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      id: 0,
      shinseiBunruiCd: "",
      shinseiBunruiNm: "",
    } as ShinseiBunruiModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      shinseiBunruiCd: [
        {
          required: true,
          validator: async (self: any, value: string, callback: any) => {
            if (!value) {
              callback(CheckMsg.CHK_SHINSEI_BUNRUI_CD);
            } else if (/\D/g.test(value)) {
              callback(CheckMsg.CHK_ISNUM);
            } else if (value.length !== 2) {
              callback(CheckMsg.CHK_SHINSEI_BUNRUI_CD_LEN);
            } else {
              try {
                await checkShinseiBunruiCd({
                  id: rowId.value,
                  databaseField: "shinsei_bunrui_cd",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "申請分類コード",
        },
      ],
      shinseiBunruiNm: [
        {
          required: true,
          message: CheckMsg.CHK_SHINSEI_BUNRUI_NM2,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            if (props.operation === DialogGenre.Add) {
              await createShinseiBunrui({
                shinseiBunruiCd: form.shinseiBunruiCd,
                shinseiBunruiNm: form.shinseiBunruiNm,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GYOUSHU_ADDED,
              });
            } else {
              await updateShinseiBunrui(form.id, {
                shinseiBunruiNm: form.shinseiBunruiNm,
              });

              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GYOUSHU_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getShinseiBunruiInfo = async (id: number) => {
      const {
        data: { result },
      } = await getShinseiBunruiById(id);
      form.id = result.id;
      form.shinseiBunruiCd = result.shinsei_bunrui_cd;
      form.shinseiBunruiNm = result.shinsei_bunrui_nm;
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getShinseiBunruiInfo(rowId.value as number);
      }
    });

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
    };
  },
});
